import React from 'react'
import styled, { css } from 'styled-components'

import { QuizDiagnosticsBookGroup } from '../types'
import {
  getSegmentFlexBasis,
  packBookGroupsIntoRows
} from '../utils/bookGroupLayout'
import {
  QUIZ_DIAGNOSTICS_BAR_MIN_WIDTH,
  QUIZ_DIAGNOSTICS_DESKTOP_MAX_WIDTH,
  QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH
} from '../utils/quizDiagnosticsBreakpoints'
import {
  QUIZ_COMPACT_CARD_MAX_CHAPTERS,
  QUIZ_DIAGNOSTICS_ALL_CARD_WIDTH,
  QUIZ_DIAGNOSTICS_COMPACT_CARD_HORIZONTAL_MARGIN,
  QUIZ_DIAGNOSTICS_COMPACT_CARD_MAX_CHAPTERS,
  QUIZ_DIAGNOSTICS_GROUP_BOX_PADDING_BOTTOM,
  QUIZ_DIAGNOSTICS_GROUP_BOX_PADDING_TOP,
  QUIZ_DIAGNOSTICS_GROUP_BOX_PADDING_TOP_NARROW,
  QUIZ_DIAGNOSTICS_ZERO_SCORE_BACKGROUND,
  QUIZ_DIAGNOSTICS_ZERO_SCORE_BACKGROUND_OPACITY,
  QUIZ_DIAGNOSTICS_ZERO_SCORE_STRIP_WIDTH,
  QUIZ_DIAGNOSTICS_CHAPTER_BAR_WIDTH
} from '../utils/quizDiagnosticsBarLayout'
import QuizDiagnosticsAll from './QuizDiagnosticsAll'
import QuizDiagnosticsBookTitle from './QuizDiagnosticsBookTitle'
import QuizDiagnosticsExampleLegend, {
  EXAMPLE_CARD_WIDTH
} from './QuizDiagnosticsExampleLegend'
import QuizDiagnosticsGroupBox from './QuizDiagnosticsGroupBox'
import QuizDiagnosticsProgressBar from './QuizDiagnosticsProgressBar'
import { useSelectQuizDiagnosticsChapter } from '../hooks/useSelectQuizDiagnosticsChapter'
import { useNavigateToQuizDiagnosticsBook } from '../hooks/useNavigateToQuizDiagnosticsBook'

type QuizDiagnosticsChaptersGridProps = {
  bookGroups: QuizDiagnosticsBookGroup[]
  overallPercent: number
}

const QuizDiagnosticsChaptersGrid = ({
  bookGroups,
  overallPercent
}: QuizDiagnosticsChaptersGridProps): JSX.Element => {
  const rows = packBookGroupsIntoRows(bookGroups)
  const isSingleBookView = bookGroups.length === 1
  const { selectChapter } = useSelectQuizDiagnosticsChapter()
  const { navigateToBookChapter } = useNavigateToQuizDiagnosticsBook()

  const handleChapterBarClick = (chapterId: string) => {
    selectChapter(chapterId)
  }

  const handleChapterTitleClick = (chapter: {
    student_book_id: string
    order: number
  }) => {
    navigateToBookChapter({
      studentBookId: chapter.student_book_id,
      chapterOrder: chapter.order
    })
  }

  return (
    <GridContainer>
      {rows?.map((row, rowIndex) => (
        <BookRow key={`quiz-diagnostics-row-${rowIndex}`}>
          {row?.includesAllSummary ? (
            <>
              <ExampleLegendSlot>
                <QuizDiagnosticsExampleLegend variant='chapter' />
              </ExampleLegendSlot>
              <AllSummarySlot>
                <QuizDiagnosticsGroupBox flexBasis='100%'>
                  <QuizDiagnosticsAll percent={overallPercent} />
                </QuizDiagnosticsGroupBox>
              </AllSummarySlot>
            </>
          ) : null}

          {row?.segments?.map((segment, segmentIndex) => {
            const isLastCardInRow = segmentIndex === row.segments.length - 1
            const useCompactLayout = isSingleBookView
              ? segment.columnSpan <= QUIZ_DIAGNOSTICS_COMPACT_CARD_MAX_CHAPTERS
              : segment.columnSpan <= QUIZ_COMPACT_CARD_MAX_CHAPTERS
            const shouldGrow =
              isLastCardInRow || Boolean(row.includesAllSummary)

            return (
              <QuizDiagnosticsGroupBox
                key={`${segment.book.bookId}-${rowIndex}-${segmentIndex}`}
                flexBasis={
                  row.includesAllSummary
                    ? '0'
                    : getSegmentFlexBasis(row, segment.columnSpan)
                }
                flexGrow={shouldGrow ? 1 : 0}
                useCompactLayout={useCompactLayout}
              >
                {segment?.chapters?.map((chapter, chapterIndex) => (
                  <BarItem
                    key={chapter.id}
                    $columns={segment.columnSpan}
                    $isZeroScore={chapter.average_score === 0}
                    $useCompactLayout={useCompactLayout}
                  >
                    <QuizDiagnosticsBookTitle>
                      {!segment.isContinuation && chapterIndex === 0
                        ? segment.book.tag
                        : null}
                    </QuizDiagnosticsBookTitle>

                    <QuizDiagnosticsProgressBar
                      chapter={chapter}
                      dueTodayCount={chapter.quizzes_due_today ?? 0}
                      color={chapter.tag_colour}
                      onBarClick={() => handleChapterBarClick(chapter.id)}
                      onTitleClick={() => handleChapterTitleClick(chapter)}
                      percent={chapter.average_score}
                      title={`${chapter.tag} ${chapter.order}`}
                      subtitle={chapter.title}
                      barWidth={QUIZ_DIAGNOSTICS_CHAPTER_BAR_WIDTH}
                    />
                  </BarItem>
                ))}
              </QuizDiagnosticsGroupBox>
            )
          })}
        </BookRow>
      ))}
    </GridContainer>
  )
}

export default QuizDiagnosticsChaptersGrid

const GridContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  min-width: 0;
  position: relative;
  z-index: 0;
  overflow: visible;
  padding-bottom: 16px;
`

const BookRow = styled.div`
  display: flex;
  flex-wrap: nowrap;
  justify-content: flex-start;
  align-items: stretch;
  gap: 8px;
  width: 100%;
  min-width: 0;
  overflow: visible;

  @media (max-width: ${QUIZ_DIAGNOSTICS_DESKTOP_MAX_WIDTH}px) {
    gap: 6px;
  }

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    flex-direction: column;
    gap: 8px;
  }
`

const ExampleLegendSlot = styled.div`
  display: flex;
  flex: 0 0 ${EXAMPLE_CARD_WIDTH}px;
  width: ${EXAMPLE_CARD_WIDTH}px;
  min-width: ${EXAMPLE_CARD_WIDTH}px;
  align-items: stretch;

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    flex: 0 0 100%;
    width: 100%;
    min-width: 0;
  }
`

const AllSummarySlot = styled.div`
  display: flex;
  flex: 0 0 ${QUIZ_DIAGNOSTICS_ALL_CARD_WIDTH}px;
  width: ${QUIZ_DIAGNOSTICS_ALL_CARD_WIDTH}px;
  min-width: ${QUIZ_DIAGNOSTICS_ALL_CARD_WIDTH}px;
  max-width: ${QUIZ_DIAGNOSTICS_ALL_CARD_WIDTH}px;
  align-items: stretch;

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    flex: 0 0 100%;
    width: 100%;
    min-width: 0;
    max-width: 100%;
  }

  > * {
    width: 100%;
  }
`

const BarItem = styled.div<{
  $columns: number
  $isZeroScore: boolean
  $useCompactLayout: boolean
}>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;

  ${({ $columns, $isZeroScore, $useCompactLayout }) =>
    $useCompactLayout
      ? css`
          flex: 0 0
            ${QUIZ_DIAGNOSTICS_ZERO_SCORE_STRIP_WIDTH +
            QUIZ_DIAGNOSTICS_COMPACT_CARD_HORIZONTAL_MARGIN * 2}px;
          width: ${QUIZ_DIAGNOSTICS_ZERO_SCORE_STRIP_WIDTH}px;
          min-width: ${QUIZ_DIAGNOSTICS_ZERO_SCORE_STRIP_WIDTH}px;
          max-width: ${QUIZ_DIAGNOSTICS_ZERO_SCORE_STRIP_WIDTH}px;
          margin-left: ${QUIZ_DIAGNOSTICS_COMPACT_CARD_HORIZONTAL_MARGIN}px;
          margin-right: ${QUIZ_DIAGNOSTICS_COMPACT_CARD_HORIZONTAL_MARGIN}px;
          align-self: flex-start;
          box-sizing: content-box;
        `
      : css`
          flex: 0 0 calc(100% / ${Math.max($columns, 1)});
          width: calc(100% / ${Math.max($columns, 1)});
          min-width: 0;
          max-width: calc(100% / ${Math.max($columns, 1)});
          align-self: ${$isZeroScore ? 'stretch' : 'flex-start'};
        `}

  &:hover,
  &:focus-within {
    z-index: ${({ theme }) => theme.zIndex.tooltip};
  }

  ${({ $isZeroScore }) =>
    $isZeroScore &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: -${QUIZ_DIAGNOSTICS_GROUP_BOX_PADDING_TOP}px;
        bottom: -${QUIZ_DIAGNOSTICS_GROUP_BOX_PADDING_BOTTOM}px;
        left: 50%;
        transform: translateX(-50%);
        width: ${QUIZ_DIAGNOSTICS_ZERO_SCORE_STRIP_WIDTH}px;
        background-color: ${QUIZ_DIAGNOSTICS_ZERO_SCORE_BACKGROUND};
        opacity: ${QUIZ_DIAGNOSTICS_ZERO_SCORE_BACKGROUND_OPACITY};
        z-index: 0;
        pointer-events: none;
      }

      @media (max-width: ${QUIZ_DIAGNOSTICS_DESKTOP_MAX_WIDTH}px) {
        &::before {
          top: -${QUIZ_DIAGNOSTICS_GROUP_BOX_PADDING_TOP_NARROW}px;
          bottom: 0;
        }
      }
    `}

  > *:last-child {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    height: 100%;
    flex: 1;
  }

  @media (max-width: ${QUIZ_DIAGNOSTICS_RESPONSIVE_MAX_WIDTH}px) {
    ${({ $columns, $useCompactLayout }) =>
      $useCompactLayout
        ? css`
            flex: 0 0
              ${QUIZ_DIAGNOSTICS_ZERO_SCORE_STRIP_WIDTH +
              QUIZ_DIAGNOSTICS_COMPACT_CARD_HORIZONTAL_MARGIN * 2}px;
            width: ${QUIZ_DIAGNOSTICS_ZERO_SCORE_STRIP_WIDTH}px;
            min-width: ${QUIZ_DIAGNOSTICS_ZERO_SCORE_STRIP_WIDTH}px;
            max-width: ${QUIZ_DIAGNOSTICS_ZERO_SCORE_STRIP_WIDTH}px;
            margin-left: ${QUIZ_DIAGNOSTICS_COMPACT_CARD_HORIZONTAL_MARGIN}px;
            margin-right: ${QUIZ_DIAGNOSTICS_COMPACT_CARD_HORIZONTAL_MARGIN}px;
            box-sizing: content-box;
          `
        : css`
            flex: 0 0
              max(
                ${QUIZ_DIAGNOSTICS_BAR_MIN_WIDTH}px,
                calc(100% / ${Math.max($columns, 1)})
              );
            width: max(
              ${QUIZ_DIAGNOSTICS_BAR_MIN_WIDTH}px,
              calc(100% / ${Math.max($columns, 1)})
            );
            min-width: ${QUIZ_DIAGNOSTICS_BAR_MIN_WIDTH}px;
            max-width: max(
              ${QUIZ_DIAGNOSTICS_BAR_MIN_WIDTH}px,
              calc(100% / ${Math.max($columns, 1)})
            );
          `}
  }
`
